<script setup lang="ts">
import { ref, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useUserStore } from '@/stores/user'
import { useOnlineStore } from '@/stores/online'
import { useRegisteredStore } from '@/stores/registered'
import { useAudioDevices } from '@/composables/useAudioDevices'
import type { AudioDeviceRole } from '@/types/types'
import LeadAvatar from '@/components/LeadAvatar.vue'
import BaseSwitch from '@/components/BaseSwitch.vue'
import { storeToRefs } from 'pinia'
import { version } from '../../package.json'

const dropdownRef = ref<HTMLUListElement | null>(null)
const isDropdownOpen = ref(false)
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const onlineStore = useOnlineStore()
const { online } = storeToRefs(onlineStore)
const registeredStore = useRegisteredStore()
const { registered } = storeToRefs(registeredStore)
const { audio, updateAudioDevicesList, selectAudioDevice } = useAudioDevices()

const autoaccept = defineModel<boolean>('autoaccept', { default: false })

// profile_picture is null unless the user uploaded one, so fall back to the
// same deterministic initials avatar the SMS screens give leads.
const photo = computed(() => user.value?.profile_picture ?? undefined)

// Green when the phone is up, crimson when it isn't.
const statusBorder = computed(() =>
  online.value && registered.value ? 'border-[#00b300]' : 'border-[#dc143c]',
)

// Both switches are permission-gated, and whichever of them shows sits in one
// group under a single divider — a rule between two toggles is noise.
const showDnd = computed(
  () => user.value?.role !== 'agent' || !!user.value?.account.is_agent_schedule,
)
const showAutoAccept = computed(() => !!user.value?.permissions?.includes('app-autoaccept'))

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value

  // The device pickers live in this menu now, so refresh the available
  // microphones/speakers each time it opens rather than once at startup.
  if (isDropdownOpen.value) {
    updateAudioDevicesList()
  }
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

onClickOutside(dropdownRef, closeDropdown)

// Bootstrap 3 .dropdown-menu > li > a metrics
const menuLink =
  'block px-[20px] py-[3px] clear-both font-normal leading-[1.42857143] text-[#333] ' +
  'whitespace-nowrap hover:text-[#262626] hover:no-underline hover:bg-[#f5f5f5] ' +
  'focus:text-[#262626] focus:no-underline focus:bg-[#f5f5f5]'

// Bootstrap 3 .form-control metrics
const formControl =
  'block w-full h-[34px] px-[12px] py-[6px] text-[14px] leading-[1.42857143] text-[#555] ' +
  'bg-white border border-[#ccc] rounded-[4px] shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] ' +
  'transition-[border-color,box-shadow] duration-150 ease-in-out ' +
  'focus:border-[#66afe9] focus:outline-0 ' +
  'focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(102,175,233,0.6)]'

const fieldLabel = 'block mb-[3px] text-[#808080] font-bold text-[12px] uppercase'

// Bootstrap 3's .divider, but at half its margin: the menu has to fit inside
// the app shell, and 6px either side of a 1px rule is a lot of nothing.
const separator = 'h-px my-[3px] overflow-hidden bg-[#e5e5e5]'

const switchRow = 'px-[20px] py-[8px]'

// Both device pickers are the same control over different halves of the audio
// settings, so they're described once and rendered in a loop. `device` is the
// resolved device to show as selected; `role` is what a pick is recorded
// against. There's deliberately no separate ringer picker — incoming calls ring
// on the speaker, since nobody wants the ring somewhere they aren't listening.
const devicePickers = [
  {
    id: 'microphone',
    label: 'Microphone',
    role: 'microphone',
    device: 'microphoneDevice',
    options: 'input',
  },
  { id: 'speaker', label: 'Speaker', role: 'speaker', device: 'speakerDevice', options: 'output' },
] as const

// Picking is a one-way trip into the composable rather than a v-model onto the
// shared state: what the select shows is the device in use, which isn't always
// the one the user chose. While a chosen headset is unplugged the picker shows
// the fallback that's actually carrying audio — a preference that isn't in the
// options would render the select blank — and it flips back on its own when the
// headset returns.
const pickDevice = (role: AudioDeviceRole, event: Event) =>
  selectAudioDevice(role, (event.target as HTMLSelectElement).value)

// Bootstrap 3 .dropdown-menu, capped to the app shell so a tall menu scrolls
// inside itself rather than being clipped by the shell's hidden overflow.
// 129px is the navbar row the menu hangs from (121px) plus bottom clearance;
// --shell-h is the shell's own height, declared in App.vue.
const dropdownMenu =
  'absolute top-full -right-[10px] min-[768px]:right-0 left-auto z-[1000] float-left ' +
  // max-w gives the header's truncating name/email a definite width to
  // resolve against, instead of letting a long one widen the whole menu.
  'min-w-[300px] max-w-[340px] py-[5px] px-0 mx-0 mb-0 mt-[2px] mr-[20px] text-[14px] text-left list-none ' +
  'bg-white bg-clip-padding border border-[rgba(0,0,0,0.15)] rounded-[4px] ' +
  'shadow-[0_6px_12px_rgba(0,0,0,0.175)] select-none ' +
  'max-h-[calc(var(--shell-h)-129px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] ' +
  'overflow-y-auto [-webkit-overflow-scrolling:touch]'
</script>

<template>
  <nav
    class="relative flow-root min-h-[70px] mb-0 z-[1100] rounded-none border-0 border-b border-transparent bg-[#073b4c] pt-[env(safe-area-inset-top,0px)]"
  >
    <a class="float-left h-[50px] p-[15px] text-[18px] leading-[20px] cursor-default" href="#">
      <img class="m-auto select-none" src="../assets/callingly-small-white2.png" />
    </a>

    <ul v-if="user?.id" class="float-right pl-0 mb-0 list-none min-[768px]:mr-[-15px]">
      <li class="relative block" ref="dropdownRef">
        <a
          href="#"
          class="relative flex items-center px-[15px] py-[10px] h-[69px] select-none bg-transparent! hover:no-underline focus:no-underline"
          role="button"
          aria-haspopup="true"
          :aria-expanded="isDropdownOpen"
          @click.stop="toggleDropdown"
        >
          <div
            id="profile"
            class="shrink-0 rounded-full border-2 mr-[10px] leading-[0]"
            :class="statusBorder"
          >
            <img
              v-if="photo"
              class="block h-[32px] w-[32px] rounded-full object-cover"
              :src="photo"
            />
            <LeadAvatar v-else :name="user?.full_name" size="sm" />
          </div>
          <span class="text-[#a7b1c2]"
            ><span
              class="max-w-[150px] overflow-hidden float-left whitespace-nowrap text-ellipsis max-[429px]:max-w-[90px]"
              >{{ user?.full_name }}</span
            >
            <i class="material-icons text-[18px]! ml-[5px] mr-[10px] float-right mt-[1px]"
              >keyboard_arrow_down</i
            ></span
          >
        </a>

        <ul v-show="isDropdownOpen" :class="dropdownMenu" @click.stop>
          <li class="px-[20px] py-[8px] flex items-center gap-[10px]">
            <!-- Out of flow against the menu itself (the only positioned
                 ancestor), so it sits in the corner without pushing the
                 header's avatar/name row around. -->
            <span
              class="pointer-events-none absolute right-[10px] top-[5px] text-[11px] text-[#a7b1c2] select-none"
            >
              v{{ version }}
            </span>
            <img
              v-if="photo"
              class="block h-[40px] w-[40px] rounded-full object-cover shrink-0"
              :src="photo"
            />
            <LeadAvatar v-else :name="user?.full_name" />
            <!-- flex-1 so the block claims the rest of the row rather than
                 sizing to its content: without it the three lines truncate
                 against their own width and leave the menu half empty. -->
            <div class="min-w-0 flex-1">
              <!-- pr clears the version in the corner, so a long name
                   truncates before it reaches it. -->
              <div class="pr-[34px] text-[15px] leading-[1.35] text-[#073b4c] font-bold truncate">
                {{ user?.full_name }}
              </div>
              <div class="text-[12px] leading-[1.35] text-[#a7b1c2] truncate">
                {{ user?.email }}
              </div>
              <div class="text-[12px] leading-[1.35] text-[#a7b1c2] truncate">
                {{ user?.phone_number_formatted }}
              </div>
            </div>
          </li>

          <template v-if="showDnd || showAutoAccept">
            <li role="separator" :class="separator" />
            <li v-if="showDnd" :class="switchRow">
              <BaseSwitch
                label="Do Not Disturb"
                :model-value="!!user?.donotdisturb"
                @update:model-value="userStore.toggleDnd"
              />
            </li>
            <li v-if="showAutoAccept" :class="switchRow">
              <BaseSwitch v-model="autoaccept" label="Auto Accept" />
            </li>
          </template>

          <li role="separator" :class="separator" />

          <li
            v-for="(picker, i) in devicePickers"
            :key="picker.id"
            :class="['px-[20px] pb-[10px]', { 'pt-[10px]': i === 0 }]"
          >
            <label :for="picker.id" :class="fieldLabel">{{ picker.label }}</label>
            <select
              :id="picker.id"
              :value="audio[picker.device]"
              :class="formControl"
              @change="pickDevice(picker.role, $event)"
            >
              <option
                v-for="option in audio[picker.options]"
                :key="option.deviceId"
                :value="option.deviceId"
              >
                {{ option.label }}
              </option>
            </select>
          </li>

          <li role="separator" :class="separator" />
          <li class="pb-[5px]">
            <a href="#" :class="menuLink" @click="userStore.logout">Log out</a>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>
